import { useMemo } from 'react';

/** Stable empty lists — supermarket openTickets/recipes rarely non-empty; avoid thrashing on []→[] identity. */
const EMPTY_LIST = Object.freeze([]);

function asList(value) {
  if (!Array.isArray(value) || value.length === 0) return EMPTY_LIST;
  return value;
}

/**
 * Builds per-domain context slices so consumers only re-render when their slice changes.
 */
export function useDataContextSlices({
  // state
  products,
  suppliers,
  purchases,
  orders,
  customers,
  sales,
  lots,
  expiredProducts,
  invoices,
  invoicePayments,
  supermarketPayments,
  supermarketInvoices,
  supplierDebts,
  customerDebts,
  customerInvoices,
  expenses,
  restaurantDishes,
  stockConsumptions,
  restaurantTables,
  openTickets,
  recipes,
  productionOrders,
  stockData,
  workerSessions,
  notifications,
  reports,
  stockLocations,
  persistentStockData,
  offlineQueue,
  businessInfo,
  userInfo,
  metadata,
  discrepancyManagement,
  inventoryData,
  currentBusinessType,
  filteredCategories,
  currentBusinessId,
  dataLoaded,
  dataShellReady,
  ready,
  // setters / actions
  setProductsWithSync,
  setProductsOnly,
  refreshProductsFromStorage,
  setSuppliersWithSync,
  setSuppliersOnly,
  refreshSuppliersFromStorage,
  setPurchasesWithSync,
    setPurchasesOnly,
    refreshPurchasesFromStorage,
    setOrdersWithSync,
    setOrdersOnly,
    refreshOrdersFromStorage,
  setCustomersWithSync,
  setCustomersOnly,
  refreshCustomersFromStorage,
  refreshCustomerDebtsFromStorage,
  setSalesWithSync,
  setSalesOnly,
  refreshSalesFromStorage,
  setLotsWithSync,
  setLotsOnly,
  refreshLotsFromStorage,
  setExpiredProductsWithSync,
  triggerExpiredProductsProcessing,
  setInvoicesWithSync,
  setInvoicePaymentsWithSync,
  setSupermarketPaymentsWithSync,
  setSupermarketInvoicesWithSync,
  setSupplierDebtsWithSync,
  setCustomerDebtsWithSync,
  setCustomerInvoicesWithSync,
  setExpensesWithSync,
  setRestaurantDishesWithSync,
  setStockConsumptionsWithSync,
  setRestaurantTablesWithSync,
  setOpenTicketsWithSync,
  setRecipesWithSync,
  setProductionOrdersWithSync,
  syncLotsWithDataContext,
  setStockDataWithSync,
  setWorkerSessionsWithSync,
  setWorkerSessionsOnly,
  refreshWorkerSessionsFromStorage,
  syncWorkerSessionsFromKv,
  setNotificationsWithSync,
  setReportsWithSync,
  setInventoryData,
  calculateInventoryData,
  setStockLocationsWithSync,
  setPersistentStockDataWithSync,
  setOfflineQueueWithSync,
  setBusinessInfoWithSync,
  setUserInfoWithSync,
  setMetadataWithSync,
  setDiscrepancyManagementWithSync,
  persistSupermarketPartialPayment,
  persistNewExpense,
  persistUpdatedExpense,
  refreshInvoicesFromStorage,
  refreshExpensesFromStorage,
  refreshSupermarketPaymentsFromStorage,
  refreshInvoicePaymentsFromStorage,
  refreshSupermarketInvoicesFromStorage,
  refreshSupplierDebtsFromStorage,
  refreshExpiredProductsFromStorage,
  refreshNotificationsFromStorage,
  refreshDiscrepancyFromStorage,
  refreshReportsFromStorage,
  syncReports
}) {
  const meta = useMemo(
    () => ({
      currentBusinessType,
      filteredCategories,
      currentBusinessId,
      dataLoaded,
      dataShellReady,
      ready,
      businessInfo,
      setBusinessInfo: setBusinessInfoWithSync,
      userInfo,
      setUserInfo: setUserInfoWithSync,
      metadata,
      setMetadata: setMetadataWithSync
    }),
    [
      currentBusinessType,
      filteredCategories,
      currentBusinessId,
      dataLoaded,
      dataShellReady,
      ready,
      businessInfo,
      setBusinessInfoWithSync,
      userInfo,
      setUserInfoWithSync,
      metadata,
      setMetadataWithSync
    ]
  );

  const catalog = useMemo(
    () => ({
      products,
      setProducts: setProductsWithSync,
      setProductsWithSync,
      setProductsOnly,
      refreshProductsFromStorage,
      lots,
      setLots: setLotsWithSync,
      setLotsWithSync,
      setLotsOnly,
      refreshLotsFromStorage,
      syncLotsWithDataContext,
      stockData,
      setStockData: setStockDataWithSync,
      setStockDataWithSync,
      stockLocations,
      setStockLocations: setStockLocationsWithSync,
      setStockLocationsWithSync,
      persistentStockData,
      setPersistentStockData: setPersistentStockDataWithSync,
      setPersistentStockDataWithSync
    }),
    [
      products,
      setProductsWithSync,
      setProductsOnly,
      refreshProductsFromStorage,
      lots,
      setLotsWithSync,
      setLotsOnly,
      refreshLotsFromStorage,
      syncLotsWithDataContext,
      stockData,
      setStockDataWithSync,
      stockLocations,
      setStockLocationsWithSync,
      persistentStockData,
      setPersistentStockDataWithSync
    ]
  );

  /**
   * Product/lot/stock setters only — stable when products[] or lots[] change.
   * Checkout, product write hooks, and lot UI-only updates should use this slice.
   */
  const catalogWriters = useMemo(
    () => ({
      setProducts: setProductsWithSync,
      setProductsWithSync,
      setProductsOnly,
      refreshProductsFromStorage,
      setLots: setLotsWithSync,
      setLotsWithSync,
      setLotsOnly,
      refreshLotsFromStorage,
      syncLotsWithDataContext,
      setStockData: setStockDataWithSync,
      setStockDataWithSync,
      setStockLocations: setStockLocationsWithSync,
      setStockLocationsWithSync,
      setPersistentStockData: setPersistentStockDataWithSync,
      setPersistentStockDataWithSync
    }),
    [
      setProductsWithSync,
      setProductsOnly,
      refreshProductsFromStorage,
      setLotsWithSync,
      setLotsOnly,
      refreshLotsFromStorage,
      syncLotsWithDataContext,
      setStockDataWithSync,
      setStockLocationsWithSync,
      setPersistentStockDataWithSync
    ]
  );

  /** Table service, open tickets, cuisine consumption — isolated from product/lot POS thrash. */
  const restaurantDishesStable = asList(restaurantDishes);
  const stockConsumptionsStable = asList(stockConsumptions);
  const restaurantTablesStable = asList(restaurantTables);
  const openTicketsStable = asList(openTickets);
  const recipesStable = asList(recipes);
  const productionOrdersStable = asList(productionOrders);

  const restaurantOps = useMemo(
    () => ({
      restaurantDishes: restaurantDishesStable,
      setRestaurantDishes: setRestaurantDishesWithSync,
      setRestaurantDishesWithSync,
      stockConsumptions: stockConsumptionsStable,
      setStockConsumptions: setStockConsumptionsWithSync,
      setStockConsumptionsWithSync,
      restaurantTables: restaurantTablesStable,
      setRestaurantTables: setRestaurantTablesWithSync,
      setRestaurantTablesWithSync,
      openTickets: openTicketsStable,
      setOpenTickets: setOpenTicketsWithSync,
      setOpenTicketsWithSync
    }),
    [
      restaurantDishesStable,
      setRestaurantDishesWithSync,
      stockConsumptionsStable,
      setStockConsumptionsWithSync,
      restaurantTablesStable,
      setRestaurantTablesWithSync,
      openTicketsStable,
      setOpenTicketsWithSync
    ]
  );

  /** Recipes + production runs — isolated from catalog product grid. */
  const recipesProduction = useMemo(
    () => ({
      recipes: recipesStable,
      setRecipes: setRecipesWithSync,
      setRecipesWithSync,
      productionOrders: productionOrdersStable,
      setProductionOrders: setProductionOrdersWithSync,
      setProductionOrdersWithSync
    }),
    [recipesStable, setRecipesWithSync, productionOrdersStable, setProductionOrdersWithSync]
  );

  const purchasesSlice = useMemo(
    () => ({
      purchases,
      setPurchases: setPurchasesWithSync,
      setPurchasesWithSync,
      setPurchasesOnly,
      refreshPurchasesFromStorage
    }),
    [purchases, setPurchasesWithSync, setPurchasesOnly, refreshPurchasesFromStorage]
  );

  const inventoryValuation = useMemo(
    () => ({
      inventoryData,
      setInventoryData,
      calculateInventoryData
    }),
    [inventoryData, setInventoryData, calculateInventoryData]
  );

  /** @deprecated Prefer catalog / purchasesSlice / inventoryValuation — kept for provider bridge. */
  const inventory = useMemo(
    () => ({
      ...catalog,
      ...purchasesSlice,
      ...inventoryValuation
    }),
    [catalog, purchasesSlice, inventoryValuation]
  );

  const salesSlice = useMemo(
    () => ({
      sales,
      setSales: setSalesWithSync,
      setSalesWithSync,
      setSalesOnly,
      refreshSalesFromStorage
    }),
    [sales, setSalesWithSync, setSalesOnly, refreshSalesFromStorage]
  );

  const salesWriters = useMemo(
    () => ({
      setSales: setSalesWithSync,
      setSalesWithSync,
      setSalesOnly,
      refreshSalesFromStorage
    }),
    [setSalesWithSync, setSalesOnly, refreshSalesFromStorage]
  );

  const customersSlice = useMemo(
    () => ({
      customers,
      setCustomers: setCustomersWithSync,
      setCustomersWithSync,
      setCustomersOnly,
      refreshCustomersFromStorage,
      refreshCustomerDebtsFromStorage,
      customerDebts,
      setCustomerDebts: setCustomerDebtsWithSync,
      customerInvoices,
      setCustomerInvoices: setCustomerInvoicesWithSync
    }),
    [
      customers,
      setCustomersWithSync,
      setCustomersOnly,
      refreshCustomersFromStorage,
      refreshCustomerDebtsFromStorage,
      customerDebts,
      setCustomerDebtsWithSync,
      customerInvoices,
      setCustomerInvoicesWithSync
    ]
  );

  const customersWriters = useMemo(
    () => ({
      setCustomers: setCustomersWithSync,
      setCustomersWithSync,
      setCustomersOnly,
      refreshCustomersFromStorage,
      refreshCustomerDebtsFromStorage,
      setCustomerDebts: setCustomerDebtsWithSync,
      setCustomerInvoices: setCustomerInvoicesWithSync
    }),
    [
      setCustomersWithSync,
      setCustomersOnly,
      refreshCustomersFromStorage,
      refreshCustomerDebtsFromStorage,
      setCustomerDebtsWithSync,
      setCustomerInvoicesWithSync
    ]
  );

  const suppliersSlice = useMemo(
    () => ({
      suppliers,
      setSuppliers: setSuppliersWithSync,
      setSuppliersWithSync,
      setSuppliersOnly,
      refreshSuppliersFromStorage
    }),
    [suppliers, setSuppliersWithSync, setSuppliersOnly, refreshSuppliersFromStorage]
  );

  const ordersSlice = useMemo(
    () => ({
      orders,
      setOrders: setOrdersWithSync,
      setOrdersWithSync,
      setOrdersOnly,
      refreshOrdersFromStorage
    }),
    [orders, setOrdersWithSync, setOrdersOnly, refreshOrdersFromStorage]
  );

  const expensesSlice = useMemo(
    () => ({
      expenses,
      setExpenses: setExpensesWithSync,
      persistNewExpense,
      persistUpdatedExpense,
      refreshExpensesFromStorage
    }),
    [
      expenses,
      setExpensesWithSync,
      persistNewExpense,
      persistUpdatedExpense,
      refreshExpensesFromStorage
    ]
  );

  const paymentsSlice = useMemo(
    () => ({
      invoices,
      setInvoices: setInvoicesWithSync,
      supermarketPayments,
      setSupermarketPayments: setSupermarketPaymentsWithSync,
      supermarketInvoices,
      setSupermarketInvoices: setSupermarketInvoicesWithSync,
      supplierDebts,
      setSupplierDebts: setSupplierDebtsWithSync,
      persistSupermarketPartialPayment,
      refreshInvoicesFromStorage,
      refreshSupermarketPaymentsFromStorage,
      refreshSupermarketInvoicesFromStorage,
      refreshSupplierDebtsFromStorage
    }),
    [
      invoices,
      setInvoicesWithSync,
      supermarketPayments,
      setSupermarketPaymentsWithSync,
      supermarketInvoices,
      setSupermarketInvoicesWithSync,
      supplierDebts,
      setSupplierDebtsWithSync,
      persistSupermarketPartialPayment,
      refreshInvoicesFromStorage,
      refreshSupermarketPaymentsFromStorage,
      refreshSupermarketInvoicesFromStorage,
      refreshSupplierDebtsFromStorage
    ]
  );

  const cashRegisterSlice = useMemo(
    () => ({
      workerSessions,
      setWorkerSessions: setWorkerSessionsWithSync,
      setWorkerSessionsWithSync,
      setWorkerSessionsOnly,
      refreshWorkerSessionsFromStorage,
      syncWorkerSessionsFromKv,
      invoicePayments,
      setInvoicePayments: setInvoicePaymentsWithSync,
      refreshInvoicePaymentsFromStorage
    }),
    [
      workerSessions,
      setWorkerSessionsWithSync,
      setWorkerSessionsOnly,
      refreshWorkerSessionsFromStorage,
      syncWorkerSessionsFromKv,
      invoicePayments,
      setInvoicePaymentsWithSync,
      refreshInvoicePaymentsFromStorage
    ]
  );

  const operationsSlice = useMemo(
    () => ({
      expiredProducts,
      setExpiredProducts: setExpiredProductsWithSync,
      triggerExpiredProductsProcessing,
      discrepancyManagement,
      setDiscrepancyManagement: setDiscrepancyManagementWithSync,
      notifications,
      setNotifications: setNotificationsWithSync,
      refreshExpiredProductsFromStorage,
      refreshNotificationsFromStorage,
      refreshDiscrepancyFromStorage
    }),
    [
      expiredProducts,
      setExpiredProductsWithSync,
      triggerExpiredProductsProcessing,
      discrepancyManagement,
      setDiscrepancyManagementWithSync,
      notifications,
      setNotificationsWithSync,
      refreshExpiredProductsFromStorage,
      refreshNotificationsFromStorage,
      refreshDiscrepancyFromStorage
    ]
  );

  const syncSlice = useMemo(
    () => ({
      offlineQueue,
      setOfflineQueue: setOfflineQueueWithSync,
      setOfflineQueueWithSync,
      reports,
      setReports: setReportsWithSync,
      refreshReportsFromStorage,
      syncReports: () => syncReports()
    }),
    [
      offlineQueue,
      setOfflineQueueWithSync,
      reports,
      setReportsWithSync,
      refreshReportsFromStorage,
      syncReports
    ]
  );

  return {
    meta,
    catalog,
    catalogWriters,
    restaurantOps,
    recipesProduction,
    purchases: purchasesSlice,
    inventoryValuation,
    inventory,
    sales: salesSlice,
    salesWriters,
    customers: customersSlice,
    customersWriters,
    suppliers: suppliersSlice,
    orders: ordersSlice,
    expenses: expensesSlice,
    payments: paymentsSlice,
    cashRegister: cashRegisterSlice,
    operations: operationsSlice,
    sync: syncSlice
  };
}
